import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { submitRedsysForm } from "../../lib/redsysForm";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

interface TarjetaRow {
  id: number;
  etiqueta: string | null;
  marca: string | null;
  ultimosDigitos: string | null;
  esPredeterminada: boolean;
  createdAt: string;
}

export function TarjetasPage() {
  const [list, setList] = useState<TarjetaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editEtiqueta, setEditEtiqueta] = useState("");
  const [revocarId, setRevocarId] = useState<number | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<TarjetaRow[]>("/cliente/tarjetas");
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleAnadir = async () => {
    setRedirecting(true);
    setError(null);
    try {
      const res = await api.post<{
        formData: {
          Ds_SignatureVersion: string;
          Ds_MerchantParameters: string;
          Ds_Signature: string;
          actionUrl: string;
        };
      }>("/cliente/tarjetas/iniciar-registro");
      submitRedsysForm(res.data.formData);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error ?? "No se pudo iniciar el registro de tarjeta");
      setRedirecting(false);
    }
  };

  const handlePredeterminada = async (id: number) => {
    try {
      await api.patch(`/cliente/tarjetas/${id}`, { es_predeterminada: true });
      await fetchList();
    } catch {
      setError("No se pudo actualizar la tarjeta predeterminada");
    }
  };

  const openEditar = (t: TarjetaRow) => {
    setEditId(t.id);
    setEditEtiqueta(t.etiqueta?.trim() ?? "");
    setError(null);
  };

  const guardarEtiqueta = async () => {
    if (editId == null) return;
    try {
      await api.patch(`/cliente/tarjetas/${editId}`, { etiqueta: editEtiqueta.trim() });
      setEditId(null);
      await fetchList();
    } catch {
      setError("No se pudo guardar la etiqueta");
    }
  };

  const handleEliminar = async () => {
    if (revocarId == null) return;
    try {
      await api.delete(`/cliente/tarjetas/${revocarId}`);
      setRevocarId(null);
      await fetchList();
    } catch {
      setError("No se pudo eliminar la tarjeta");
    }
  };

  const labelTarjeta = (t: TarjetaRow) => {
    const marca = t.marca?.trim() || "Tarjeta";
    const digitos = t.ultimosDigitos ? ` ···${t.ultimosDigitos}` : "";
    const etiq = t.etiqueta?.trim() ? ` (${t.etiqueta})` : "";
    return `${marca}${digitos}${etiq}`;
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Mis tarjetas</h1>
        <Button onClick={() => void handleAnadir()} loading={redirecting} disabled={redirecting}>
          Añadir tarjeta
        </Button>
      </div>

      <p className="text-sm text-gray-600">
        Los cobros automáticos de tus seguros usarán la tarjeta predeterminada.
      </p>

      {redirecting && (
        <p className="text-sm text-[#1A3AB8]">Redirigiendo al banco…</p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-500">Cargando…</p>
      ) : list.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <p className="text-[#2C2C2C]">No tienes ninguna tarjeta configurada.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {list.map((t) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4"
            >
              <div>
                <p className="font-medium text-[#2C2C2C]">{labelTarjeta(t)}</p>
                {t.esPredeterminada && (
                  <span className="mt-1 inline-block rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                    Predeterminada
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {!t.esPredeterminada && (
                  <Button size="sm" variant="secondary" onClick={() => void handlePredeterminada(t.id)}>
                    Establecer como predeterminada
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => openEditar(t)}>
                  Etiqueta
                </Button>
                <Button size="sm" variant="danger" onClick={() => setRevocarId(t.id)}>
                  Eliminar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editId != null && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <Input
            label="Etiqueta"
            value={editEtiqueta}
            onChange={(e) => setEditEtiqueta(e.target.value)}
            placeholder="Ej. Tarjeta empresa"
          />
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setEditId(null)}>
              Cancelar
            </Button>
            <Button onClick={() => void guardarEtiqueta()}>Guardar</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={revocarId != null}
        onClose={() => setRevocarId(null)}
        onConfirm={() => handleEliminar()}
        title="Eliminar tarjeta"
        message="La tarjeta dejará de usarse para cobros automáticos. ¿Continuar?"
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}
